import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import { TtButton } from '@23/react'

import {
  ModalComponent,
  Button,
  If,
  Then,
  Else,
  TooltipComponent,
} from '../../../sharedLegacy/components'
import usePlaybackIntent from '../../hooks/usePlaybackIntent'
import {
  setEnded,
  setIsWebinarLive,
} from '../../../firebase/hooks/useWebinarGlobalState'

import './WebinarTopbarComponent.scss'

const getDisabledLiveTriggerTooltip = (
  isStreaming,
  togglingInProgress,
  liveStatus,
  streamingStartFailed,
  isInGearMode,
  isDraft,
  isHeadlessPresent,
  isTemplate
) => {
  if (isTemplate) {
    return intl.formatMessage({
      defaultMessage:
        'This webinar room is only for filling out content, you cannot go live with a template.',
    })
  }
  if (!isHeadlessPresent && !isInGearMode) {
    return intl.formatMessage({
      defaultMessage: 'The webinar cloud mixer is starting up. Please wait.',
    })
  }

  if (isDraft) {
    return intl.formatMessage({
      defaultMessage: 'Going live in Draft Mode is not possible.',
    })
  }

  if (streamingStartFailed) {
    return intl.formatMessage({
      defaultMessage: 'Failed to connect. Refresh the page to try again',
    })
  }

  if (!isStreaming) {
    if (isInGearMode) {
      return intl.formatMessage({
        defaultMessage: 'Waiting for you to start streaming',
      })
    }

    return intl.formatMessage({ defaultMessage: 'Waiting for connection' })
  }

  if (togglingInProgress) {
    return intl.formatMessage({
      defaultMessage: 'Switching to live is in progress',
    })
  }

  if (liveStatus === 'previous') {
    return intl.formatMessage({ defaultMessage: 'The webinar has finished.' })
  }

  return undefined
}

const getGoToManagePage = (onClick) => (
  <Button color="grey" onClick={onClick} size="small">
    <FormattedMessage defaultMessage="Go To Manage Page" />
  </Button>
)

const renderLiveTrigger = (properties, onGoLiveClick) => {
  const {
    isStreaming,
    isBroadcasting,
    role,
    togglingInProgress,
    liveStatus,
    showEndWebinarConfirmModal,
    streamingStartFailed,
    isDraft,
    endButtonState,
    liveButtonState,
    webinarEnded,
    redirectToManagePage,
    isEndingWebinar,
    isInGearMode,
    isLiveButtonDisabled,
    isHeadlessPresent,
    isTemplate,
  } = properties

  if (role === 'presenter') {
    return null
  }

  if (webinarEnded) {
    return getGoToManagePage(redirectToManagePage)
  }

  if (!isBroadcasting && !isEndingWebinar) {
    return (
      <>
        {isDraft && getGoToManagePage(redirectToManagePage)}
        <TooltipComponent
          title={getDisabledLiveTriggerTooltip(
            isStreaming,
            togglingInProgress,
            liveStatus,
            streamingStartFailed,
            isInGearMode,
            isDraft,
            isHeadlessPresent,
            isTemplate
          )}
        >
          <TtButton
            className="live-toggle-button"
            size="small"
            disabled={
              isTemplate ||
              (isLiveButtonDisabled && liveButtonState !== 'loading')
            }
            loading={liveButtonState === 'loading'}
            onClick={liveButtonState === 'loading' ? () => {} : onGoLiveClick}
          >
            {isTemplate ? (
              <FormattedMessage defaultMessage="Template" />
            ) : (
              <FormattedMessage defaultMessage="Go Live" />
            )}
          </TtButton>
        </TooltipComponent>
      </>
    )
  }

  if (endButtonState === 'loading') {
    return (
      <TooltipComponent
        title={intl.formatMessage({ defaultMessage: 'Ending the webinar' })}
      >
        <TtButton
          className="live-toggle-button"
          size="small"
          color="danger"
          loading
        >
          <FormattedMessage defaultMessage="End webinar" />
        </TtButton>
      </TooltipComponent>
    )
  }
  return (
    <TtButton
      className="live-toggle-button"
      size="small"
      color="danger"
      disabled={isLiveButtonDisabled}
      onClick={showEndWebinarConfirmModal}
      loading={endButtonState === 'loading'}
    >
      <FormattedMessage defaultMessage="End webinar" />
    </TtButton>
  )
}

const WebinarTopbarComponent = (props) => {
  const {
    hideEndWebinarConfirmModal,
    isEndWebinarConfirmModalVisible,
    onLiveToggle,
    children,
    isInGearMode,
  } = props

  const { setPlaybackStopIntent } = usePlaybackIntent()

  const onGoLiveClick = useCallback(() => {
    onLiveToggle(true)
  }, [onLiveToggle])

  const onEndWebinarClick = useCallback(() => {
    // TODO: shouldn't you only need one of them?
    setIsWebinarLive(false)
    setEnded()
    onLiveToggle(false)
    setPlaybackStopIntent()
  }, [onLiveToggle, setPlaybackStopIntent])

  return (
    <div className="webinar-topbar-component">
      <div className="flex">
        {children}
        {renderLiveTrigger(props, onGoLiveClick)}
      </div>
      <ModalComponent
        visible={isEndWebinarConfirmModalVisible}
        wrapClassName="end-live-confirm"
        okText={intl.formatMessage({ defaultMessage: 'End Webinar' })}
        cancelText={intl.formatMessage({
          defaultMessage: 'No, I am not done yet',
        })}
        onOk={onEndWebinarClick}
        onCancel={hideEndWebinarConfirmModal}
        okButtonProps={{ color: 'red' }}
        closable={false}
        showFooter
      >
        <h3>
          <FormattedMessage defaultMessage="Are you sure you want to end this webinar?" />
        </h3>
        <ul>
          <li>
            <If condition={isInGearMode}>
              <Then>
                <FormattedMessage defaultMessage="This will stop the live streaming for your audience." />
              </Then>
              <Else>
                <FormattedMessage defaultMessage="This will stop the live streaming for you, other speakers and for your audience." />
              </Else>
            </If>
          </li>
          <li>
            <FormattedMessage defaultMessage="You will not be able to go live again." />
          </li>
          <li>
            <FormattedMessage defaultMessage="We will start preparing your recording and reports." />
          </li>
        </ul>
      </ModalComponent>
    </div>
  )
}

WebinarTopbarComponent.propTypes = {
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  role: PropTypes.oneOf(['presenter', 'producer']),
  onLiveToggle: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  liveStatus: PropTypes.oneOf(['upcoming', 'live', 'previous']),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  liveButtonState: PropTypes.oneOf(['idle', 'loading']),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  endButtonState: PropTypes.oneOf(['idle', 'loading']),
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  togglingInProgress: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  showEndWebinarConfirmModal: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  streamingStartFailed: PropTypes.bool,
  hideEndWebinarConfirmModal: PropTypes.func,
  isEndWebinarConfirmModalVisible: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isDraft: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  webinarEnded: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  redirectToManagePage: PropTypes.func,
  children: PropTypes.node,
  isInGearMode: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isBroadcasting: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isStreaming: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isEndingWebinar: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isHeadlessPresent: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  isLiveButtonDisabled: PropTypes.bool,
}

export default WebinarTopbarComponent
